import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getWebApp, openExternalUrl, openInvoiceUrl, setMainButtonText } from '@/lib/telegram';
import { rub } from '@/lib/currency';
import { api, ApiError, isProviderError } from '@/lib/api';
import { newNonce } from '@/lib/utils';
import { providerLabel } from '@/lib/providers';
import { minorToAmount } from '@/lib/currency';
import { Card, CardContent } from '@/components/ui/card';
import { GlyphChip, IconChip, SectionLabel, OptionRow } from '@/components/ui/calm';
import { SkeletonCard, SkeletonPage } from '@/components/ui/skeleton';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toast } from 'sonner';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Check, Zap, CreditCard, Wallet, AlertTriangle } from 'lucide-react';
import type { Plan } from '@/api/types';
import { track } from '@/lib/analytics';

export const Route = createFileRoute('/checkout/$planId')({
  component: Checkout,
});

const promoSchema = z.object({
  code: z.string().min(3, 'too short').max(20, 'too long'),
});

function Checkout() {
  const { planId } = Route.useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: profile } = useQuery({ queryKey: ['profile'], queryFn: () => api.profile() });
  const { data: plans, isPending: termPending } = useQuery({ queryKey: ['plans'], queryFn: () => api.plans() });
  // Тариф «Навсегда» живёт в своём разделе: в общем списке его нет.
  const lifetimeEnabled = api.getAuth().lifetimeEnabled;
  const { data: lifetimePlans, isPending: lifetimePending } = useQuery({
    queryKey: ['plans', 'lifetime'],
    queryFn: () => api.plans('lifetime'),
    enabled: lifetimeEnabled,
  });
  const plan: Plan | undefined =
    plans?.find((p) => p.id === planId) ?? lifetimePlans?.find((p) => p.id === planId);
  const plansPending = termPending || (lifetimeEnabled && lifetimePending && !plan);
  const { data: providersData } = useQuery({
    queryKey: ['payment-providers'],
    queryFn: () => api.paymentProviders(),
  });
  // Способ по умолчанию — первый из списка сервера (порядок кнопок задаёт бэк, СБП
  // первым). Раньше тут стоял 'cryptobot', и он оставался выбранным у всех, у кого
  // включён: из мини-аппа за 25–27.09 ушло 38 счетов CryptoBot, оплачен один.
  const [provider, setProvider] = useState<string | null>(null);
  const providerList = providersData?.providers ?? [];

  useEffect(() => {
    // 'balance' — валидный выбор, но его НЕТ в providersData.providers (API отдаёт
    // только внешних провайдеров). Без этого исключения эффект мгновенно откатывал
    // выбор баланса на первый способ — метод было невозможно выбрать.
    if (!providersData) return;
    const list = providersData.providers;
    if (provider === 'balance') return;
    if (provider === null || !list.includes(provider)) {
      setProvider(list[0] ?? null);
    }
  }, [providersData, provider]);

  useEffect(() => {
    track('checkout_opened', { plan_id: planId });
  }, [planId]);
  // discountDisplay — скидка уже в display-валюте (whole units, e.g. рубли).
  // Для percent: храним сам процент, считаем от priceRub. Для fixed: конвертим USD-cents → display whole.
  const [appliedPromo, setAppliedPromo] = useState<
    | { code: string; type: 'percent'; percent: number }
    | { code: string; type: 'fixed'; discountDisplay: number }
    | null
  >(null);

  /* Цену считает СЕРВЕР, а не мы.
   *
   * Раньше считали здесь: `цена × (1 − процент)`. У скидки за счёт партнёра бэк
   * применял свои правила, и экран показывал «0 ₽» там, где счёт выставлялся на
   * другую сумму, а при полной скидке провайдер вообще отбивал счёт («Не удалось
   * создать счёт»). `appliedPromo` остался только для подписи под полем ввода. */
  const { data: quote, isPending: quotePending } = useQuery({
    queryKey: ['price-quote', planId, appliedPromo?.code ?? null],
    queryFn: () => api.priceQuote(planId),
    enabled: !!plan,
  });

  // Две цены, потому что сбор за оплату берётся один раз — на входе денег. Счётом
  // человек платит со сбором, с баланса — без него: сбор уже уплачен при пополнении.
  const balancePrice = quote
    ? Math.round(quote.final_display_minor / 100)
    : (plan?.balancePriceRub ?? 0);
  // Сумма счёта выбранного способа: сервер считает её той же формулой, что и сам
  // счёт (`FeePolicy.invoice_total_minor`). Раньше кнопка писала цену без сбора
  // способа, и 119 ₽ на кнопке превращались в 125.78 ₽ на странице провайдера.
  const invoiceMinor =
    provider && provider !== 'balance' ? quote?.provider_totals?.[provider] : undefined;
  const invoicePrice = invoiceMinor != null
    ? minorToAmount(invoiceMinor)
    : String(
        quote
          ? Math.round((quote.total_display_minor || quote.final_display_minor) / 100)
          : (plan?.priceRub ?? 0),
      );
  const feeIncluded = (providersData?.fee_percent ?? 0) > 0;
  const finalPrice = provider === 'balance' ? String(balancePrice) : invoicePrice;
  // Число для аналитики. Строка на экране с разрядами через неразрывный пробел, и
  // `Number(finalPrice)` давал NaN на любом счёте от 1000 ₽.
  const finalPriceRub =
    provider === 'balance' ? balancePrice : invoiceMinor != null ? invoiceMinor / 100 : Number(invoicePrice);
  const promoDiscountAmount = quote ? Math.round(quote.discount_display_minor / 100) : 0;
  // Скидка покрыла всю стоимость: платить нечего, счёт не выпускаем.
  const isFree = quote?.is_free === true;
  // Ref на актуальную цену — onMain-замыкание в TG MainButton иначе может схватить
  // stale finalPrice если юзер применил промо между ре-рендерами (биометрия
  // показывает confirm async, успевает прийти новый finalPrice).
  const finalPriceRef = useRef(finalPrice);
  finalPriceRef.current = finalPrice;
  const isFreeRef = useRef(isFree);
  isFreeRef.current = isFree;
  const planNameRef = useRef('');
  planNameRef.current = plan?.name ?? '';

  // Партнёр раздал скидку глубже своей маржи, а доплатить ему нечем. Молча показать
  // полную цену нельзя: человек только что видел код применённым.
  useEffect(() => {
    if (quote?.promo_blocked_reason === 'partner_funds') {
      toast.error(t('Promo unavailable'), { duration: 8000 });
    }
  }, [quote?.promo_blocked_reason, t]);

  const { register, handleSubmit, formState: { errors }, setError, clearErrors } = useForm<{ code: string }>({
    resolver: zodResolver(promoSchema),
  });

  // Подарочный код партнёра, введённый в поле промокода. Скидки не даёт — даёт
  // бесплатную подписку, поэтому уходит не в `appliedPromo`, а в отдельное
  // подтверждение (как кнопка «Активировать?» в боте).
  const { mutate: redeemVoucher, isPending: redeeming } = useMutation({
    mutationFn: (code: string) => api.redeemVoucher(code),
    onSuccess: async (res) => {
      await qc.invalidateQueries();
      toast.success(t('Voucher activated', { plan: res.plan_name }));
      navigate({ to: '/success/$subscriptionId', params: { subscriptionId: res.subscription_id } });
    },
    onError: (e: Error) => {
      setError('code', { type: 'manual', message: e.message });
    },
  });

  const { mutate: validatePromo, isPending } = useMutation({
    mutationFn: (code: string) => api.applyPromo(code),
    onSuccess: async (data) => {
      clearErrors('code');
      if (data.kind === 'voucher') {
        const { confirmAction } = await import('@/lib/telegram');
        const ok = await confirmAction(
          t('Voucher free', { plan: data.plan_name ?? t('Plan word') }),
          t('Voucher title'),
          { okText: t('Activate'), destructive: false },
        );
        if (ok) redeemVoucher(data.code);
        return;
      }
      if (data.discount_type === 'percent') {
        setAppliedPromo({ code: data.code, type: 'percent', percent: data.discount_value });
        toast.success(t('Promo discount toast', { discount: `${data.discount_value}%` }));
        track('promo_applied', { discount_type: 'percent' });
      } else {
        // fixed: discount_value в USD-cents, конвертим в display-валюту whole units.
        // usd_cents * usd_to_display_rate = display_minor; / 100 = display whole.
        const discountDisplay = Math.floor((data.discount_value * data.usd_to_display_rate) / 100);
        setAppliedPromo({ code: data.code, type: 'fixed', discountDisplay });
        toast.success(t('Promo discount toast', { discount: `−${discountDisplay} ₽` }));
        track('promo_applied', { discount_type: 'fixed' });
      }
    },
    onError: (err: Error) => {
      setError('code', { type: 'manual', message: err.message });
      setAppliedPromo(null);
      // Только код ошибки бэка: сам промокод в аналитику не уходит.
      track('promo_failed', { code: err instanceof ApiError ? err.code : 'error' });
    },
  });

  // Замок на повторный тап. MainButton.showProgress() дизейблит кнопку, но между
  // тапом и появлением прогресса есть окно: биометрия и confirm — асинхронные,
  // висят секундами. Без замка второй тап за это время создаёт второй счёт.
  const payBusyRef = useRef(false);

  // Метка одного открытого чекаута: дабл-тап возвращает тот же счёт, а новый заход
  // (вторая подписка того же тарифа) создаёт новый. Ключ по planId: роутер
  // переиспользует компонент при смене параметра.
  const nonce = useMemo(() => newNonce(), [planId]);

  const { mutate: payNow, isPending: paying } = useMutation({
    mutationFn: () =>
      api.createPaymentIntent({ plan_uuid: planId, provider: provider ?? '', idempotency_key: nonce }),
    onMutate: () => {
      track('payment_initiated', {
        plan_id: planId,
        provider: provider ?? '',
        price_rub: finalPriceRub,
      });
    },
    onSettled: () => {
      payBusyRef.current = false;
    },
    onSuccess: (intent) => {
      // Промокод сгорел между preview и создавием intent'а (race под нагрузкой).
      // Бэк уже снял active_promo_code_id и создал invoice на полную цену. Алертим
      // и продолжаем — invoice уже есть и юзер увидит реальную сумму в TG-инвойсе.
      if (intent.promo_exhausted) {
        toast.warning(t('Promo exhausted'), { duration: 6000 });
      }
      // CryptoBot возвращает t.me/CryptoBot/app?... — открываем через openTelegramLink
      // чтобы остаться внутри Telegram (mini-app). Внешние провайдеры → openLink.
      openInvoiceUrl(intent.invoice_url);
      navigate({ to: '/processing/$paymentId', params: { paymentId: intent.payment_id } });
    },
    onError: (e: Error) => {
      toast.error(t('Invoice failed detail', { error: e.message }));
    },
  });

  /* Оплата с баланса. Считаем от ИТОГОВОЙ цены, а не от полной: скидка теперь
   * применяется и здесь, и запрет «баланс + промокод» снят. При полной скидке
   * списание нулевое, поэтому этим же путём оформляется бесплатная выдача. */
  const balanceDisplay = (profile?.balance_display_minor ?? 0) / 100;
  // balancePayable — баланс в принципе применим к этому заказу. Если применим, но не
  // хватает — показываем строку задизейбленной с суммой нехватки и ведём на пополнение
  // (не прячем метод, иначе юзер не понимает куда делся).
  const balancePayable = !!plan && balancePrice > 0;
  // Достаточность решает сервер в USD-центах, как и сама проверка при списании.
  // Рублёвое сравнение здесь расходилось с ней на копейки округления (заявка #78).
  const balanceEnough =
    balancePayable && (quote ? quote.balance_enough : balanceDisplay >= balancePrice);
  const balanceShortfallRub = Math.max(0, Math.ceil(balancePrice - balanceDisplay));

  const { mutate: payFromBalance, isPending: payingBalance } = useMutation({
    mutationFn: () => api.buyFromBalance(planId),
    onMutate: () => {
      track('payment_initiated', { plan_id: planId, provider: 'balance', price_rub: plan?.priceRub ?? 0 });
    },
    onSettled: () => {
      payBusyRef.current = false;
    },
    onSuccess: async (res) => {
      // Свежие данные сразу (баланс/подписки/QR) — как чинили в processing.
      await qc.invalidateQueries();
      navigate({ to: '/success/$subscriptionId', params: { subscriptionId: res.subscription_id } });
    },
    onError: (e: Error) => {
      toast.error(isProviderError(e) ? t('Provider unavailable') : e.message || t('Balance pay failed'));
    },
  });

  // Если выбран баланс, но он перестал быть доступным (применили промо) — сбросим выбор.
  useEffect(() => {
    if (provider === 'balance' && !balanceEnough) setProvider(providerList[0] ?? null);
  }, [provider, balanceEnough, providerList]);

  const hasPlan = plan !== undefined;
  // Текст кнопки считаем в рендере, а эффект зависит от готовой строки. Иначе deps
  // тянули `plan`/`plans` — новые объекты на каждый фоновый refetch react-query, и
  // эффект перезапускался вхолостую, отправляя Telegram лишние web_app_setup_main_button
  // (на загрузке экрана их уходило четыре). Клиент применяет их асинхронно, поэтому
  // на телефоне кнопка успевала показать предыдущий выбор.
  const mainButtonLabel = isFree
    ? t('Get for free')
    : provider === 'balance'
      ? t('Pay from balance', { price: finalPrice })
      : t('Pay via', { price: finalPrice, provider: providerLabel(provider ?? '').toUpperCase() });

  useEffect(() => {
    const tg = getWebApp();
    if (!tg?.BackButton) return;
    tg.BackButton.show();
    const onBack = () => navigate({ to: '/plans' });
    tg.BackButton.onClick(onBack);
    return () => tg.BackButton.offClick(onBack);
  }, [navigate]);

  // Прогресс — отдельным эффектом: он меняется в момент оплаты и не должен тащить за
  // собой переустановку текста и обработчика.
  useEffect(() => {
    const tg = getWebApp();
    if (!tg?.MainButton) return;
    // showProgress() без leaveActive: Telegram сам дизейблит кнопку на время запроса.
    // showProgress(true) оставлял её активной — второй тап уходил в оплату.
    if (paying || payingBalance) tg.MainButton.showProgress();
    else tg.MainButton.hideProgress();
  }, [paying, payingBalance]);

  useEffect(() => {
    const tg = getWebApp();
    if (!tg?.MainButton) return;
    // Пока тарифы едут, цена ещё 0 — кнопка «ОПЛАТИТЬ 0 ₽» мелькала на каждом входе
    // в экран. Показываем её только когда есть что оплачивать.
    //
    // Ждём и цену с сервера: до ответа на кнопке стоит цена БЕЗ скидки, и тап по ней
    // отправил бы человека платить полную сумму по коду, который он только что ввёл.
    // Способ оплаты тоже ждём: без него на кнопке нечего написать и некуда платить.
    if (!hasPlan || quotePending || (!isFree && provider === null)) {
      tg.MainButton.hide();
      return;
    }

    // Текст и видимость — одним setParams вместо setText + show. Два отдельных вызова
    // = два события в клиент, и между ними кнопка живёт с новым текстом, но старым
    // состоянием видимости.
    setMainButtonText(mainButtonLabel, { is_visible: true });
    // Block E.7: Face ID / отпечаток перед оплатой.
    // Fail-closed: если биометрия недоступна — показываем явный showPopup-confirm,
    // никогда не пропускаем оплату без подтверждения юзера.
    // ВСЕГДА вызываем authenticate() напрямую — он сам сделает mount + isAvailable
    // в правильном порядке (isAvailable до mount всегда возвращает false).
    const onMain = async () => {
      if (payBusyRef.current) return;
      payBusyRef.current = true;
      const { biometric, confirmAction } = await import('@/lib/telegram');
      // Спрашивать ли биометрию — выбор клиента. При null (ещё не выбирал) предлагаем
      // один раз здесь и запоминаем: раньше Face ID запрашивался у всех и всегда,
      // отключить это было нельзя.
      let useBiometric = api.getAuth().biometricConfirmEnabled;
      if (useBiometric === null) {
        // Это вопрос про настройку, а не про деньги: красная кнопка тут пугала.
        useBiometric = await confirmAction(
          t('Biometry ask'),
          t('Biometry title'),
          { okText: t('Yes'), destructive: false },
        );
        // Ошибку сохранения глотаем: спросим в следующий раз, ронять оплату нельзя.
        void api.setBiometricConfirm(useBiometric).catch(() => {});
      }
      const result = useBiometric
        ? await biometric.authenticate(t('Confirm purchase'))
        : 'unavailable';
      // Биометрия — только УСКОРЕНИЕ, а не обязалово. 'ok' → сразу оплачиваем.
      // Любой другой исход (юзер не дал доступ к Face ID / отменил скан / биометрии
      // нет на устройстве) НЕ блокирует покупку — показываем явный TG-попап-конфирм.
      // Это остаётся fail-closed: без явного «Да» в попапе оплата не пройдёт. Раньше
      // отказ в доступе к биометрии наглухо лочил покупку — юзер не мог купить вообще.
      if (result !== 'ok') {
        // Читаем актуальную цену из ref'a — за время биометрии юзер мог применить промо.
        // С баланса деньги списываются сразу и без отката, поэтому попап говорит, что
        // именно купят и откуда спишут. Счёт ещё можно не оплатить, ему хватает суммы.
        const ok = await confirmAction(
          isFreeRef.current
            ? t('Get for free confirm')
            : provider === 'balance'
              ? t('Pay plan from balance', {
                  plan: planNameRef.current,
                  amount: rub(finalPriceRef.current),
                })
              : t('Pay amount', { amount: finalPriceRef.current }),
          t('Payment confirm title'),
        );
        if (!ok) {
          payBusyRef.current = false;
          return;
        }
      }
      // Полная скидка и оплата с баланса идут одной ручкой: списание там считается от
      // итоговой цены, при нуле оно нулевое. Отдельного пути «выдать бесплатно» нет
      // намеренно — два пути выдачи с одним смыслом у нас уже расходились.
      if (isFreeRef.current || provider === 'balance') payFromBalance();
      else payNow();
    };
    tg.MainButton.onClick(onMain);

    return () => {
      tg.MainButton.offClick(onMain);
      tg.MainButton.hideProgress();
    };
  }, [mainButtonLabel, provider, hasPlan, quotePending, isFree, payNow, payFromBalance]);

  // Пока тарифы едут, plan ещё undefined. Показывать «тариф не найден» в этот момент
  // нельзя: человек открыл экран оплаты после перезахода и решит, что покупка сорвалась.
  if (plansPending) return <CheckoutSkeleton />;

  if (!plan) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[70vh] p-6 text-center space-y-5">
        <IconChip icon={AlertTriangle} tone="amber" size={56} radius={16} />
        <div className="space-y-2">
          <h2 className="text-[25px] font-extrabold tracking-[-0.8px] text-foreground">{t('Plan gone')}</h2>
          <p className="text-sm text-[#8a8c93]">
            {t('Plan gone desc')}
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate({ to: '/plans' })}
          className="calm-cta px-6 py-3 rounded-xl text-sm font-bold"
        >
          {t('To plans')}
        </button>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6 pb-24">
      <div>
        <SectionLabel className="mb-2">{t('Order Summary')}</SectionLabel>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            {plan.emoji ? (
              <GlyphChip glyph={plan.emoji} size={42} />
            ) : (
              <IconChip icon={Zap} tone="blue" size={42} />
            )}
            <div className="min-w-0 flex-1">
              <div className="font-extrabold text-foreground">{plan.name}</div>
              <div className="text-sm text-[#8a8c93]">
                {plan.isLifetime
                  ? t('Order plan lifetime', { devices: plan.devices })
                  : t('Order plan', { devices: plan.devices })}
              </div>
            </div>
            <div className="text-right shrink-0">
              {/* Зачёркиваем по ФАКТУ скидки с сервера, а не по факту «код введён»:
                  код мог не примениться, и перечёркнутая цена рядом с той же суммой
                  выглядела бы поломкой. */}
              {promoDiscountAmount > 0 && (
                <div className="text-xs text-[#8a8c93] line-through tabular">{rub(plan.priceRub)}</div>
              )}
              <div className="font-extrabold text-xl text-accent tabular">
                {isFree ? t('Free price') : `${finalPrice}\u00a0₽`}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div>
        <SectionLabel className="mb-3">{t('Payment Method')}</SectionLabel>
        <div className="space-y-2">
          {balancePayable && (
            balanceEnough ? (
              <OptionRow
                icon={Wallet}
                iconTone="blue"
                title={t('From balance')}
                selected={provider === 'balance'}
                onSelect={() => setProvider('balance')}
                trailing={
                  <span className="text-xs text-[#5fe3ad]">
                    {feeIncluded ? rub(balancePrice) : `${rub(Math.floor(balancePrice))} · 0%`}
                  </span>
                }
              />
            ) : (
              <OptionRow
                icon={Wallet}
                iconTone="neutral"
                title={t('From balance')}
                subtitle={t('Not enough topup', { amount: balanceShortfallRub })}
                onSelect={() => navigate({ to: '/topup' })}
                trailing={<span className="text-xs text-accent">{t('Top up arrow')}</span>}
              />
            )
          )}
          {providerList.map((p) => {
            // Сбор уже сидит в цене и одинаков у всех способов — показывать «+5%»
            // на кнопке значило бы обещать надбавку, которой не будет.
            const pct = feeIncluded ? 0 : (providersData?.provider_fee_percent?.[p] ?? 0);
            return (
              <OptionRow
                key={p}
                icon={CreditCard}
                iconTone="neutral"
                title={providerLabel(p)}
                selected={provider === p}
                onSelect={() => setProvider(p)}
                trailing={
                  pct > 0 ? (
                    <span className="text-xs text-[#8a8c93]">
                      {t('Fee badge', { percent: pct.toLocaleString(i18n.language) })}
                    </span>
                  ) : undefined
                }
              />
            );
          })}
          {providersData && providerList.length === 0 && (
            <div className="text-sm text-[#8a8c93] p-4 calm-card rounded-xl">
              {t('No providers support')}
            </div>
          )}
        </div>
        {/* Итог выбора словами, прямо под способами (#210). Кнопка Telegram на Android
            перерисовывается с запозданием, и человек должен видеть, за что и через что
            платит, не дожидаясь её. */}
        {!isFree && provider !== null && hasPlan && !quotePending ? (
          <p className="px-1 pt-3 text-sm text-[#8a8c93]" aria-live="polite">
            {provider === 'balance'
              ? t('Checkout total balance', { price: finalPrice })
              : t('Checkout total via', { price: finalPrice, provider: providerLabel(provider) })}
          </p>
        ) : null}
      </div>

      <div className="calm-card rounded-2xl p-4">
        <SectionLabel className="mb-3">{t('Promo Code')}</SectionLabel>
        <form onSubmit={handleSubmit((d) => validatePromo(d.code))} className="flex gap-2">
          <input
            {...register('code')}
            disabled={isPending || redeeming || appliedPromo !== null}
            placeholder={t('Promo placeholder')}
            className="w-full bg-white/5 border border-white/5 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50 text-foreground placeholder-[#8a8c93]"
          />
          <button
            type="submit"
            disabled={isPending || redeeming || appliedPromo !== null}
            className="calm-cta px-5 py-3 rounded-xl text-sm font-bold flex items-center justify-center min-w-[80px] active:scale-95 transition-transform disabled:opacity-50"
          >
            {isPending || redeeming ? <Loader2 className="w-4 h-4 animate-spin" /> : appliedPromo ? <Check className="w-4 h-4" /> : t('Apply')}
          </button>
        </form>
        {errors.code && <p className="text-[#f0656a] text-xs mt-2">{errors.code.message}</p>}
        {appliedPromo && promoDiscountAmount > 0 && (
          <p className="text-[#5fe3ad] text-xs mt-2 font-medium flex items-center gap-1.5">
            <Check className="w-3 h-3" />
            {/* Размер скидки берём с сервера: процент от тарифа и реальная скидка это
                не одно и то же, когда её оплачивает партнёр. */}
            {t('Promo applied', {
              code: appliedPromo.code,
              discount: `−${rub(promoDiscountAmount)}`,
            })}
          </p>
        )}
      </div>

      <TermsLine />
    </div>
  );
}

/** «Оплачивая, вы соглашаетесь с соглашением» — ссылкой на документ этого бота (#208).
 *  Тот же /info, что у экрана FAQ: react-query отдаёт его из кэша, второго запроса нет.
 *  Ссылки нет (старый бэк) — прежняя строка без ссылки. */
function TermsLine() {
  const { t } = useTranslation();
  const { data } = useQuery({ queryKey: ['client-info'], queryFn: () => api.info() });
  const url = data?.terms_url;
  if (!url) {
    return <div className="text-xs text-[#8a8c93] pt-4 text-center">{t('Terms agreement')}</div>;
  }
  return (
    <div className="text-xs text-[#8a8c93] pt-4 text-center">
      {t('Terms agreement prefix')}{' '}
      <button
        type="button"
        onClick={() => openExternalUrl(url)}
        className="underline decoration-white/25 underline-offset-2 active:opacity-70"
      >
        {t('Terms agreement link')}
      </button>
      .
    </div>
  );
}

/**
 * Заголовки секций рисуем настоящим текстом: они статичные, ждать их нечего,
 * а плейсхолдер вместо них добавил бы мельтешения там, где его быть не должно.
 */
function CheckoutSkeleton() {
  const { t } = useTranslation();
  return (
    <SkeletonPage className="space-y-6">
      <div>
        <SectionLabel className="mb-2">{t('Order Summary')}</SectionLabel>
        <SkeletonCard className="h-[74px] rounded-[24px]" />
      </div>
      <div>
        <SectionLabel className="mb-3">{t('Payment Method')}</SectionLabel>
        <div className="space-y-2">
          <SkeletonCard className="h-[58px] min-h-0 rounded-xl" />
          <SkeletonCard className="h-[58px] min-h-0 rounded-xl" />
        </div>
      </div>
      <SkeletonCard className="h-[116px] rounded-2xl" />
    </SkeletonPage>
  );
}
